import React, { useState } from 'react';

import { Select } from '@aline/common';
import { MenuItem, Typography } from '@mui/material';
import { ToolbarSelect } from 'src/components';
import { CarbonCopyIcon } from 'src/components/icons/CarbonCopyIcon';
import { SignatureFieldIcon } from 'src/components/icons/SignatureFieldIcon';
import type { ActionType, Assignee } from 'src/hooks';
import { useAllowedActionTypes } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const actionTypeDisplayName: Record<ActionType, string> = {
  cc: 'Carbon copy',
  field: 'Signer',
};

const useStyles = makeStyles()(theme => ({
  menu: {
    '&&': {
      height: theme.appSizes.inputHeight,
    },
    minWidth: 160,
  },
  menuItem: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  select: {
    '&&': {
      height: theme.appSizes.inputHeight,
      width: 160,
    },
  },
}));

interface ActionDropdownProps {
  assignee: Assignee;
  chosenAction: ActionType | null;
  onSetChosenAction: (action: ActionType) => Promise<void>;
  workflowId: UUID;
  disabled?: boolean;
}

export function ActionDropdown({
  assignee,
  chosenAction,
  disabled,
  onSetChosenAction,
  workflowId,
}: ActionDropdownProps): JSX.Element {
  const { classes } = useStyles();
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const { availableActionsForRecipient } = useAllowedActionTypes({ assignee, workflowId });

  return (
    <ToolbarSelect
      disableBorderColor
      MenuProps={{
        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        transformOrigin: { horizontal: 'left', vertical: 'top' },
      }}
      aria-label="Action"
      className={classes.select}
      classes={{ select: classes.menu }}
      customRenderValue={chosenAction ? actionTypeDisplayName[chosenAction] : 'Select Action'}
      disabled={disabled}
      menuAttribute={Select.workflow.detail.steps.action.actionDropdownMenu}
      open={menuOpen}
      selectAttribute={Select.workflow.detail.steps.action.actionDropdown}
      spanAttribute="action-select-span"
      value={chosenAction}
      onClose={() => setMenuOpen(false)}
      onOpen={() => setMenuOpen(true)}
    >
      {AvailableRecipientActions({
        actionTypes: availableActionsForRecipient,
        onSetChosenAction: async action => {
          setMenuOpen(false);
          await onSetChosenAction(action);
        },
      })}
    </ToolbarSelect>
  );
}

interface AvailableRecipientActionsProps {
  actionTypes: ActionType[];
  onSetChosenAction: (action: ActionType) => void;
}

/**
 * MUI expects MenuItem to be a *direct descendant* of Menu (to forwardRef) so we return JSX.Element[].
 */
function AvailableRecipientActions({
  actionTypes,
  onSetChosenAction,
}: AvailableRecipientActionsProps): JSX.Element[] {
  const { classes } = useStyles();

  function getDataCy(actionType: ActionType) {
    switch (actionType) {
      case 'cc':
        return Select.workflow.detail.steps.action.carbonCopy;

      case 'field':
        return Select.workflow.detail.steps.action.sign;
    }
  }

  if (!actionTypes.length)
    return [
      <MenuItem key={0} dense className={classes.menuItem}>
        <Typography>No available actions for recipient</Typography>
      </MenuItem>,
    ];

  return actionTypes.map((actionType, index) => (
    <MenuItem
      key={index}
      dense
      className={classes.menuItem}
      data-cy={getDataCy(actionType)}
      onClick={() => onSetChosenAction(actionType)}
    >
      <ActionTypeIcon actionType={actionType} />
      <Typography>{actionType ? actionTypeDisplayName[actionType] : 'Select Action'}</Typography>
    </MenuItem>
  ));
}

interface ActionTypeIconProps extends React.HTMLAttributes<HTMLElement> {
  actionType: ActionType;
}

export function ActionTypeIcon({ actionType, className }: ActionTypeIconProps): JSX.Element {
  switch (actionType) {
    case 'cc':
      return <CarbonCopyIcon className={className} />;
    case 'field':
      return <SignatureFieldIcon className={className} />;
  }
}
